import React, { useEffect, useState } from "react";
import tyreImg from "../../../../../Assets/vehicle/tyre.png";
import { Circle, Gauge, Ruler, FileText, Truck } from "lucide-react";
import { GiFlatTire } from "react-icons/gi";

// Fixed tractor tyre positions (always 6)
const TRACTOR_TYRES = ['FL', 'FR', 'DLO', 'DLI', 'DRI', 'DRO'];

// Tractor axle layout: each row has [left tyres, right tyres]
const TRACTOR_AXLES = [
  { label: 'Steer Axle', left: ['FL'], right: ['FR'] },
  { label: 'Drive Axle', left: ['DLO', 'DLI'], right: ['DRI', 'DRO'] },
];

// Trailer type → number of axles (4 tyres per axle)
const TRAILER_TYPE_MAP = {
  '20ft_container': { label: '20 ft Container', axles: 2 },
  '40ft_container': { label: '40 ft Container', axles: 4 },
  'multi_axle':     { label: 'Multi Axle',      axles: 3 },
  'heavy':          { label: 'Heavy',            axles: 5 },
};

// Generate trailer tyre IDs for given axle count
// e.g. 2 axles → T1LO, T1LI, T1RI, T1RO, T2LO, T2LI, T2RI, T2RO
const generateTrailerTyreIds = (axleCount) => {
  const ids = [];
  for (let a = 1; a <= axleCount; a++) {
    ids.push(`T${a}LO`, `T${a}LI`, `T${a}RI`, `T${a}RO`);
  }
  return ids;
};

// Build trailer axle rows for rendering
const getTrailerAxles = (axleCount) => {
  const axles = [];
  for (let a = 1; a <= axleCount; a++) {
    axles.push({
      label: `Trailer Axle ${a}`,
      left: [`T${a}LO`, `T${a}LI`],
      right: [`T${a}RI`, `T${a}RO`],
    });
  }
  return axles;
};

// Classify vehicle based on ULIP data
// CASE 1 (AUTO_LAYOUT): ULIP knows trailer + axles → show full diagram directly
// CASE 2 (SEMI_TRAILER_SELECTION): Only prime mover → ask user for trailer type
function classifyPrimeMover(v) {
  if (!v) return { mode: "SEMI_TRAILER_SELECTION", tractorTyres: 6, ui: "ASK_TRAILER" };

  const cls = (v.rc_vh_class_desc || "").toUpperCase();
  const body = (v.rc_body_type_desc || "").toUpperCase();
  const permit = (v.rc_permit_type || "").toUpperCase();
  const axles = Number(v.rc_no_of_axle || 0);

  // CASE 1: Full vehicle known (trailer + axles from RC)
  if (axles > 0 || body.includes("TRAILER") || permit.includes("TRAILER")) {
    const axleCount = axles || 6;
    return {
      mode: "AUTO_LAYOUT",
      axleCount,
      tyreCount: 2 + (axleCount - 1) * 4,
      ui: "SHOW_DIAGRAM_ONLY"
    };
  }

  // CASE 2: Tractor head only
  if (cls.includes("PRIME MOVER")) {
    return {
      mode: "SEMI_TRAILER_SELECTION",
      tractorTyres: 6,
      ui: "ASK_TRAILER"
    };
  }

  // Default to semi mode
  return {
    mode: "SEMI_TRAILER_SELECTION",
    tractorTyres: 6,
    ui: "ASK_TRAILER"
  };
}

const TyreBox = ({ title, active, psi, temp, onClick }) => (
  <div 
    className={`w-[60px] h-[72px] md:w-[70px] md:h-[85px] rounded-[8px] p-[4px] md:p-[5px] text-center text-[9px] md:text-[10px] cursor-pointer select-none flex flex-col justify-center ${
      active 
        ? 'bg-[#d9f5e4] border-[#4caf50] text-black' 
        : 'border-[#f2b6b6] text-[#e57373]'
    }`} 
    style={{ border: '1px solid' }}
    onClick={onClick}
  >
    <div className="font-bold mb-0.5">{title}</div>
    <div className="flex justify-between items-center">
      <span className="font-semibold text-[10px] md:text-xs">{psi ?? "--"}</span>
      <span className="font-semibold text-[10px] md:text-xs">PSI</span>
    </div>
    <div className="flex justify-between items-center">
      <span className="font-semibold text-[10px] md:text-xs">{temp ?? "--"}</span>
      <span className="font-semibold text-[10px] md:text-xs">°C</span>
    </div>
  </div>
);

// Renders a single axle row: [left tyres] [center spacer/image area] [right tyres]
const AxleRow = ({ axle, tyreData, openForm, showLabel }) => (
  <div className="flex flex-col items-center gap-1">
    {showLabel && (
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{axle.label}</span>
    )}
    <div className="flex items-center justify-center gap-2">
      {/* Left tyres */}
      <div className="flex gap-[6px]">
        {axle.left.map(id => (
          <TyreBox
            key={id}
            title={id}
            active={tyreData[id]?.psi != null && tyreData[id]?.psi !== ""}
            psi={tyreData[id]?.psi}
            temp={tyreData[id]?.temp}
            onClick={() => openForm(id)}
          />
        ))}
      </div>
      {/* Center spacer (axle line) */}
      <div className="w-[60px] md:w-[80px] h-[4px] bg-gray-300 rounded-full" />
      {/* Right tyres */}
      <div className="flex gap-[6px]">
        {axle.right.map(id => (
          <TyreBox
            key={id}
            title={id}
            active={tyreData[id]?.psi != null && tyreData[id]?.psi !== ""}
            psi={tyreData[id]?.psi}
            temp={tyreData[id]?.temp}
            onClick={() => openForm(id)}
          />
        ))}
      </div>
    </div>
  </div>
);

const TyreImage = ({ onPrevious, formData, setFormData, onNext, onSubmit, isSubmitting }) => {
  // Classify vehicle based on ULIP data from DriverDetailsStep
  const vehicleMode = classifyPrimeMover(formData.vehicleUlipData);
  const isAutoLayout = vehicleMode.mode === "AUTO_LAYOUT";
  // For AUTO_LAYOUT: trailer axles = total axles - 2 (steer + drive)
  const autoTrailerAxleCount = isAutoLayout ? Math.max(vehicleMode.axleCount - 2, 0) : 0;

  const [withTrailer, setWithTrailer] = useState(false);
  const [trailerType, setTrailerType] = useState('');
  const [selectedTyreId, setSelectedTyreId] = useState(null);
  
  const [tyreForm, setTyreForm] = useState({
    condition: "",
    pressure: "",
    tread: "",
    temperature: "",
    additionalNotes: "",
  });

  // Initialize tractor tyres on mount; for AUTO_LAYOUT also init trailer tyres
  useEffect(() => {
    setFormData(prev => {
      const updatedTyres = { ...(prev.tyres || {}) };
      TRACTOR_TYRES.forEach(id => {
        if (!updatedTyres[id]) updatedTyres[id] = {};
      });
      // AUTO_LAYOUT: auto-initialize trailer tyres from RC axle count
      if (isAutoLayout && autoTrailerAxleCount > 0) {
        const trailerIds = generateTrailerTyreIds(autoTrailerAxleCount);
        trailerIds.forEach(id => {
          if (!updatedTyres[id]) updatedTyres[id] = {};
        });
      }
      return { ...prev, tyres: updatedTyres };
    });
  }, [isAutoLayout, autoTrailerAxleCount]);

  // When trailer type changes, generate & initialize tyre positions
  useEffect(() => {
    if (!trailerType) return;
    const axleCount = TRAILER_TYPE_MAP[trailerType]?.axles || 0;
    const trailerIds = generateTrailerTyreIds(axleCount);
    
    setFormData(prev => {
      const updatedTyres = { ...(prev.tyres || {}) };
      // Keep tractor tyres, add trailer tyres
      trailerIds.forEach(id => {
        if (!updatedTyres[id]) updatedTyres[id] = {};
      });
      return { ...prev, tyres: updatedTyres };
    });
  }, [trailerType]);

  const handleTrailerToggle = (enabled) => {
    setWithTrailer(enabled);
    if (!enabled) {
      setTrailerType('');
      // Remove trailer tyres from formData, keep only tractor tyres
      setFormData(prev => {
        const updatedTyres = {};
        TRACTOR_TYRES.forEach(id => {
          updatedTyres[id] = prev.tyres?.[id] || {};
        });
        return { ...prev, tyres: updatedTyres };
      });
    }
  };

  const tyreData = formData.tyres || {};

  const openForm = (id) => {
    const t = tyreData[id] || {};
    setTyreForm({
      condition: t.condition || "",
      pressure: t.psi || "",
      tread: t.tread || "",
      temperature: t.temp || "",
      additionalNotes: t.notes || "",
    });
    setSelectedTyreId(id);
  };

  const handleFormChange = (key) => (e) => {
    setTyreForm(prev => ({ ...prev, [key]: e.target.value }));
  };

  const handleFormSubmit = () => {
    setFormData(prev => ({
      ...prev,
      tyres: {
        ...prev.tyres,
        [selectedTyreId]: {
          ...prev.tyres?.[selectedTyreId],
          psi: tyreForm.pressure === "" ? null : Number(tyreForm.pressure),
          temp: tyreForm.temperature === "" ? null : Number(tyreForm.temperature),
          condition: tyreForm.condition,
          tread: tyreForm.tread === "" ? null : Number(tyreForm.tread),
          notes: tyreForm.additionalNotes,
        }
      }
    }));
    setSelectedTyreId(null);
  };

  const handleFormClose = () => {
    setSelectedTyreId(null);
  };

  // Compute trailer axle rows — AUTO uses RC data, SEMI uses user selection
  const trailerAxleCount = isAutoLayout
    ? autoTrailerAxleCount
    : (trailerType ? (TRAILER_TYPE_MAP[trailerType]?.axles || 0) : 0);
  const trailerAxles = getTrailerAxles(trailerAxleCount);

  // ---------- Tyre Inspection Form ----------
  if (selectedTyreId) {
    return (
      <>
        <div className="space-y-5 pb-24">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={handleFormClose}
              className="hover:bg-gray-100 p-2 rounded-full transition"
            >
              ←
            </button>
            <h2 className="text-lg font-semibold">Tyre Inspection - {selectedTyreId}</h2>
          </div>

          {/* Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Condition <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <span className="w-5 flex-shrink-0 flex items-center justify-center"><Circle size={18} className="text-blue-500" /></span>
              <select
                value={tyreForm.condition}
                onChange={handleFormChange("condition")}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select condition</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
                <option value="Damaged">Damaged</option>
                <option value="Spare">Spare</option>
              </select>
            </div>
          </div>

          {/* Pressure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pressure (PSI) <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <span className="w-5 flex-shrink-0 flex items-center justify-center"><Gauge size={18} className="text-blue-500" /></span>
              <input
                type="number"
                value={tyreForm.pressure}
                onChange={handleFormChange("pressure")}
                placeholder="e.g., 87"
                step="0.1"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperature (°C) <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <span className="w-5 flex-shrink-0 flex items-center justify-center text-blue-500 text-lg font-bold">°</span>
              <input
                type="number"
                value={tyreForm.temperature}
                onChange={handleFormChange("temperature")}
                placeholder="e.g., 98"
                step="0.1"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Tread */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tread (mm) <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <span className="w-5 flex-shrink-0 flex items-center justify-center"><Ruler size={18} className="text-blue-500" /></span>
              <input
                type="number"
                value={tyreForm.tread}
                onChange={handleFormChange("tread")}
                placeholder="e.g., 5"
                step="0.1"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <div className="flex items-start gap-3">
              <span className="w-5 flex-shrink-0 flex items-center justify-center mt-2"><FileText size={18} className="text-blue-500" /></span>
              <textarea
                value={tyreForm.additionalNotes}
                onChange={handleFormChange("additionalNotes")}
                placeholder="Write notes..."
                rows={3}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:relative md:border-t-0 md:mt-8 md:gap-4">
          <button
            onClick={handleFormClose}
            className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
          >
            Back
          </button>
          <button
            onClick={handleFormSubmit}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm md:text-base shadow-md"
          >
            Save
          </button>
        </div>
      </>
    );
  }

  // ---------- Vehicle Layout (Row-Based) ----------
  return (
    <>
      <div className="pb-24 overflow-x-hidden">
        {/* Subheading */}
        <div className="pb-3 pl-2 mb-4 border-b border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <GiFlatTire className="text-xl text-blue-600" />
            Tyre Inspection
          </h3>
        </div>

        {/* AUTO_LAYOUT Banner — full vehicle detected from RC */}
        {isAutoLayout && (
          <div className="mb-4 mx-2 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Truck size={20} className="text-green-600" />
              <span className="text-sm font-bold text-green-800">Vehicle Detected</span>
            </div>
            <p className="text-sm font-semibold text-green-700">
              {vehicleMode.axleCount} Axle Articulated Truck
            </p>
            <p className="text-xs text-green-600 mt-0.5">
              {vehicleMode.tyreCount} Tyres (From RC) — Tap tyre to inspect
            </p>
          </div>
        )}

        {/* Tractor Section — truck image in center with tyres on sides */}
        <div className="mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-center mb-3">Prime Mover</p>
          <div className="flex justify-center items-center mt-2 overflow-hidden">
            {/* Left tyres */}
            <div className="flex flex-col gap-3 md:gap-4 mr-[-25px] md:mr-[-35px] z-10">
              <div className="flex justify-end mt-[-16px] md:mt-[-20px]">
                <TyreBox
                  title="FL"
                  active={tyreData.FL?.psi != null && tyreData.FL?.psi !== ""}
                  psi={tyreData.FL?.psi}
                  temp={tyreData.FL?.temp}
                  onClick={() => openForm("FL")}
                />
              </div>
              <div className="flex gap-[4px] md:gap-[6px]">
                <TyreBox
                  title="DLO"
                  active={tyreData.DLO?.psi != null && tyreData.DLO?.psi !== ""}
                  psi={tyreData.DLO?.psi}
                  temp={tyreData.DLO?.temp}
                  onClick={() => openForm("DLO")}
                />
                <TyreBox
                  title="DLI"
                  active={tyreData.DLI?.psi != null && tyreData.DLI?.psi !== ""}
                  psi={tyreData.DLI?.psi}
                  temp={tyreData.DLI?.temp}
                  onClick={() => openForm("DLI")}
                />
              </div>
            </div>

            {/* Truck image */}
            <img src={tyreImg} alt="vehicle" className="w-[120px] md:w-[160px] mx-0 h-[260px] md:h-[340px] pointer-events-none flex-shrink-0" />

            {/* Right tyres */}
            <div className="flex flex-col gap-3 md:gap-4 ml-[-25px] md:ml-[-35px] z-10">
              <TyreBox
                title="FR"
                active={tyreData.FR?.psi != null && tyreData.FR?.psi !== ""}
                psi={tyreData.FR?.psi}
                temp={tyreData.FR?.temp}
                onClick={() => openForm("FR")}
              />
              <div className="flex gap-[4px] md:gap-[6px]">
                <TyreBox
                  title="DRI"
                  active={tyreData.DRI?.psi != null && tyreData.DRI?.psi !== ""}
                  psi={tyreData.DRI?.psi}
                  temp={tyreData.DRI?.temp}
                  onClick={() => openForm("DRI")}
                />
                <TyreBox
                  title="DRO"
                  active={tyreData.DRO?.psi != null && tyreData.DRO?.psi !== ""}
                  psi={tyreData.DRO?.psi}
                  temp={tyreData.DRO?.temp}
                  onClick={() => openForm("DRO")}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Trailer Toggle — only shown in SEMI mode (Case 2) */}
        {!isAutoLayout && (
          <div className="mt-6 px-2">
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <Truck size={20} className="text-blue-600" />
                <span className="font-medium text-gray-800">With Trailer</span>
              </div>
              <button
                type="button"
                onClick={() => handleTrailerToggle(!withTrailer)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                  withTrailer ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    withTrailer ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Trailer Type Selector */}
            {withTrailer && (
              <div className="mt-4 bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm font-medium text-gray-700 mb-3">Select trailer type</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(TRAILER_TYPE_MAP).map(([key, { label, axles }]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTrailerType(key)}
                      className={`py-2.5 px-3 rounded-lg text-sm font-semibold transition border ${
                        trailerType === key
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-400'
                      }`}
                    >
                      <div>{label}</div>
                      <div className="text-[10px] font-normal mt-0.5 opacity-70">{axles} axle{axles > 1 ? 's' : ''} · {axles * 4} tyres</div>
                    </button>
                  ))}
                </div>
                {trailerType && (
                  <p className="mt-3 text-xs text-blue-700 font-medium">
                    {TRAILER_TYPE_MAP[trailerType].label} — {trailerAxleCount} axle{trailerAxleCount > 1 ? 's' : ''}, {trailerAxleCount * 4} tyres
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Trailer Axle Rows */}
        {trailerAxles.length > 0 && (
          <div className="mt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-center mb-3">Trailer</p>
            <div className="flex flex-col items-center gap-5">
              {trailerAxles.map((axle, idx) => (
                <AxleRow key={idx} axle={axle} tyreData={tyreData} openForm={openForm} showLabel />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:relative md:border-t-0 md:mt-8 md:gap-4">
        <button
          onClick={onPrevious}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Previous
        </button>
        <button
          onClick={onNext}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm md:text-base shadow-md"
        >
          Next
        </button>
      </div>
    </>
  );
};

export default TyreImage;
